// client/src/coms/personalisation/PersonalisationProvider.js
// One place for layout, theme and per-server preferences. Account preferences live on the server
// (/api/coms/me/prefs); localStorage keeps a per-account cache so the right theme paints before the request
// returns, and it is never the source of truth. Writes are debounced and use the server revision so two
// devices cannot silently overwrite each other.
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { comsApiDelete, comsApiGet, comsApiPatch, comsApiPost, comsApiPut } from '../comsApi';
import { DEFAULT_PREFS, sanitizePrefs, sanitizeServerOverrides, themeAccessibility } from './prefsSchema';
import { applyPresetToLayout, clone, markCustomIfChanged, resolveEffective, setPath } from './layoutModel';
import { applyThemeVars, buildThemeVars, clearThemeVars, prefersReducedMotion, resolveTheme } from './themeEngine';
import { getComsSidebarWidth, MOBILE_QUERY } from '../components/comslayout_parts/layoutUtils';
import { applyMobilePreset, defaultMobile, layoutForMobile, setMobileValue as withMobileValue, withMobileValues } from './mobileLayout';

const SAVE_DELAY_MS = 700;
const CACHE_PREFIX = 'vx:prefs:u:';
const LAST_THEME_KEY = 'vx:lastThemeVars';

const PersonalisationContext = createContext(null);
const NO_OVERRIDES = Object.freeze({});

function readJson(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

// Prefs a user had before account sync existed (read only; legacy keys are never modified or removed).
function legacySeed() {
  const prefs = clone(DEFAULT_PREFS);
  // A width the user set by dragging (the old resize hook used 350-550 px). Without one, the default (290 px)
  // matches what the old flexible layout rendered on common screens.
  let stored = null;
  try {
    stored = window.localStorage.getItem('__pa_coms_sidebar_width');
  } catch {}
  const w = getComsSidebarWidth();
  if (stored != null && Number.isFinite(w)) prefs.layout.sidebar.width = Math.min(550, Math.max(350, w));
  return sanitizePrefs(prefs);
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    try {
      return !!window.matchMedia(query).matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia(query);
    } catch {
      return undefined;
    }
    const on = () => setMatches(!!mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener('change', on);
    else if (mq.addListener) mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on);
      else if (mq.removeListener) mq.removeListener(on);
    };
  }, [query]);
  return matches;
}

export function PersonalisationProvider({ user, children }) {
  const userId = user && user.id != null ? String(user.id) : '';
  const cacheKey = userId ? `${CACHE_PREFIX}${userId}` : '';

  const [globalPrefs, setGlobalPrefs] = useState(() => {
    const cached = cacheKey ? readJson(cacheKey) : null;
    return sanitizePrefs(cached && cached.prefs ? cached.prefs : legacySeed());
  });
  const [serverOverrides, setServerOverrides] = useState(() => {
    const cached = cacheKey ? readJson(cacheKey) : null;
    return cached && cached.servers && typeof cached.servers === 'object' ? cached.servers : {};
  });
  const [loaded, setLoaded] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [themes, setThemes] = useState([]);
  const [activeServerKey, setActiveServerKey] = useState('');
  const [serverTheme, setServerTheme] = useState({ key: '', theme: null, canEdit: false, loaded: false });
  const [temp, setTemp] = useState({});
  const [layoutEditing, setLayoutEditing] = useState(null); // { snapshot } while "Customise layout" is open
  const [themePreview, setThemePreview] = useState(null); // a theme object previewed live in the editor

  const revisionRef = useRef(0);
  const pendingRef = useRef([]); // mutations not yet saved, re-applied on a revision conflict
  const saveTimerRef = useRef(null);
  const serverSaveTimersRef = useRef(new Map());
  const latestGlobalRef = useRef(globalPrefs);
  latestGlobalRef.current = globalPrefs;
  const latestOverridesRef = useRef(serverOverrides);
  latestOverridesRef.current = serverOverrides;

  // ---------- load ----------
  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    setLoaded(false);
    const cached = readJson(`${CACHE_PREFIX}${userId}`);
    setGlobalPrefs(sanitizePrefs(cached && cached.prefs ? cached.prefs : legacySeed()));
    setServerOverrides(cached && cached.servers ? cached.servers : {});
    (async () => {
      try {
        const res = await comsApiGet('/me/prefs');
        if (!alive) return;
        revisionRef.current = Number(res.revision) || 0;
        setGlobalPrefs(res.stored ? sanitizePrefs(res.prefs) : legacySeed());
        setServerOverrides(res.servers && typeof res.servers === 'object' ? res.servers : {});
        setSyncError('');
      } catch (e) {
        if (alive && e && e.status !== 401) setSyncError('Could not load your saved layout; using this device’s copy.');
      } finally {
        if (alive) setLoaded(true);
      }
      try {
        const t = await comsApiGet('/me/themes');
        if (alive) setThemes(Array.isArray(t.themes) ? t.themes : []);
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, [userId]);

  // Per-account cache (debounced with the save) and live sync between this browser's tabs.
  const writeCache = useCallback(() => {
    if (!cacheKey) return;
    writeJson(cacheKey, { prefs: latestGlobalRef.current, servers: latestOverridesRef.current, at: Date.now() });
  }, [cacheKey]);

  useEffect(() => {
    if (!cacheKey) return undefined;
    const onStorage = (e) => {
      if (!e || e.key !== cacheKey || !e.newValue) return;
      try {
        const v = JSON.parse(e.newValue);
        if (v && v.prefs) setGlobalPrefs(sanitizePrefs(v.prefs));
        if (v && v.servers) setServerOverrides(v.servers);
      } catch {}
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [cacheKey]);

  // ---------- save ----------
  const flushGlobal = useCallback(async () => {
    saveTimerRef.current = null;
    if (!userId) return;
    const pending = pendingRef.current;
    pendingRef.current = [];
    try {
      const res = await comsApiPut('/me/prefs', { prefs: latestGlobalRef.current, base_revision: revisionRef.current });
      revisionRef.current = Number(res.revision) || revisionRef.current + 1;
      setSyncError('');
    } catch (e) {
      if (e && e.status === 409 && e.data && e.data.prefs) {
        // Another device saved first: start from its copy and replay this device's changes on top.
        revisionRef.current = Number(e.data.revision) || 0;
        let merged = sanitizePrefs(e.data.prefs);
        for (const fn of pending) merged = sanitizePrefs(fn(merged));
        setGlobalPrefs(merged);
        latestGlobalRef.current = merged;
        try {
          const again = await comsApiPut('/me/prefs', { prefs: merged, base_revision: revisionRef.current });
          revisionRef.current = Number(again.revision) || revisionRef.current + 1;
        } catch {
          setSyncError('Your layout changes could not be saved. They will be retried.');
          pendingRef.current = [...pending, ...pendingRef.current];
        }
      } else if (e && e.status !== 401) {
        setSyncError('Your layout changes could not be saved. They will be retried.');
        pendingRef.current = [...pending, ...pendingRef.current];
        saveTimerRef.current = setTimeout(() => flushGlobal(), 5000);
      }
    }
    writeCache();
  }, [userId, writeCache]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => flushGlobal(), SAVE_DELAY_MS);
  }, [flushGlobal]);

  useEffect(() => {
    // Leaving or reloading the page right after a change: save it with a request that outlives the page.
    const onHide = () => {
      if (!saveTimerRef.current || !userId) return;
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      writeCache();
      fetch('/api/coms/me/prefs', {
        method: 'PUT',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prefs: latestGlobalRef.current, base_revision: revisionRef.current }),
      }).catch(() => {});
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        flushGlobal();
      }
    };
  }, [flushGlobal, userId, writeCache]);

  // updateGlobal(fn): fn(prefs) -> prefs. Every change goes through here so conflicts can replay it.
  const updateGlobal = useCallback(
    (fn) => {
      setGlobalPrefs((prev) => {
        const next = sanitizePrefs(fn(prev));
        latestGlobalRef.current = next;
        return next;
      });
      pendingRef.current.push(fn);
      scheduleSave();
    },
    [scheduleSave]
  );

  const setLayoutValue = useCallback(
    (path, value) => updateGlobal((p) => ({ ...p, layout: markCustomIfChanged(setPath(p.layout, path, value), path) })),
    [updateGlobal]
  );

  const setLayoutPreset = useCallback((presetId) => updateGlobal((p) => ({ ...p, layout: applyPresetToLayout(p.layout, presetId) })), [updateGlobal]);

  const resetLayout = useCallback(
    () => updateGlobal((p) => ({ ...p, layout: clone(DEFAULT_PREFS.layout) })),
    [updateGlobal]
  );

  const setThemeValue = useCallback((path, value) => updateGlobal((p) => ({ ...p, theme: setPath(p.theme, path, value) })), [updateGlobal]);

  const setTabsValue = useCallback((path, value) => updateGlobal((p) => ({ ...p, tabs: setPath(p.tabs, path, value) })), [updateGlobal]);

  // Mobile layout: only ever touches prefs.mobile, so desktop and mobile layouts never overwrite each other.
  const setMobileValue = useCallback((key, value) => updateGlobal((p) => ({ ...p, mobile: withMobileValue(p.mobile || defaultMobile(), key, value) })), [updateGlobal]);
  const setMobilePreset = useCallback((presetId) => updateGlobal((p) => ({ ...p, mobile: applyMobilePreset(p.mobile || defaultMobile(), presetId) })), [updateGlobal]);
  // Restoring the defaults keeps the layouts the user saved (and the text size and gesture switch stay theirs too).
  const resetMobile = useCallback(() => updateGlobal((p) => ({ ...p, mobile: { ...defaultMobile(), saved: ((p.mobile && p.mobile.saved) || []).slice() } })), [updateGlobal]);
  const setMobileValues = useCallback((values) => updateGlobal((p) => ({ ...p, mobile: withMobileValues(p.mobile || defaultMobile(), values) })), [updateGlobal]);
  // Recovery: ?resetMobileLayout=1 restores the mobile defaults once the saved prefs have loaded (for a layout that
  // got in the way), then removes itself from the address.
  useEffect(() => {
    if (!loaded) return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('resetMobileLayout') !== '1') return;
      resetMobile();
      url.searchParams.delete('resetMobileLayout');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {}
  }, [loaded, resetMobile]);

  // ---------- per-server overrides ----------
  const saveServer = useCallback(
    (serverKey) => {
      const timers = serverSaveTimersRef.current;
      if (timers.has(serverKey)) clearTimeout(timers.get(serverKey));
      timers.set(
        serverKey,
        setTimeout(async () => {
          timers.delete(serverKey);
          const ov = latestOverridesRef.current[serverKey] || {};
          try {
            const res = Object.keys(ov).length
              ? await comsApiPut(`/me/prefs/servers/${encodeURIComponent(serverKey)}`, { overrides: ov })
              : await comsApiDelete(`/me/prefs/servers/${encodeURIComponent(serverKey)}`);
            const confirmed = sanitizeServerOverrides((res && res.overrides) || {});
            setServerOverrides((prev) => {
              const next = { ...prev };
              if (Object.keys(confirmed).length) next[serverKey] = confirmed;
              else delete next[serverKey];
              latestOverridesRef.current = next;
              return next;
            });
            setSyncError('');
          } catch (e) {
            if (e && e.status !== 401) setSyncError('Your settings for this server could not be saved.');
          }
          writeCache();
        }, SAVE_DELAY_MS)
      );
    },
    [writeCache]
  );

  // Per-server overrides changed in the last moment before the page goes away (reload, close, account
  // switch) are sent with keepalive requests instead of being lost with their debounce timers.
  useEffect(() => {
    const flushServers = () => {
      const timers = serverSaveTimersRef.current;
      if (!timers.size || !userId) return;
      for (const [serverKey, t] of timers) {
        clearTimeout(t);
        const ov = latestOverridesRef.current[serverKey] || {};
        const url = `/api/coms/me/prefs/servers/${encodeURIComponent(serverKey)}`;
        fetch(url, {
          method: Object.keys(ov).length ? 'PUT' : 'DELETE',
          credentials: 'include',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: Object.keys(ov).length ? JSON.stringify({ overrides: ov }) : undefined,
        }).catch(() => {});
      }
      timers.clear();
      writeCache();
    };
    window.addEventListener('pagehide', flushServers);
    return () => {
      window.removeEventListener('pagehide', flushServers);
      flushServers();
    };
  }, [userId, writeCache]);

  // value === undefined removes the override (the setting inherits from the account again).
  const setServerValue = useCallback(
    (serverKey, key, value) => {
      const sk = String(serverKey || '').trim();
      if (!sk) return;
      setServerOverrides((prev) => {
        const cur = { ...(prev[sk] || {}) };
        if (value === undefined) delete cur[key];
        else cur[key] = value;
        const clean = sanitizeServerOverrides(cur);
        const next = { ...prev };
        if (Object.keys(clean).length) next[sk] = clean;
        else delete next[sk];
        latestOverridesRef.current = next;
        return next;
      });
      saveServer(sk);
    },
    [saveServer]
  );

  const resetServer = useCallback(
    (serverKey) => {
      const sk = String(serverKey || '').trim();
      if (!sk) return;
      setServerOverrides((prev) => {
        const next = { ...prev };
        delete next[sk];
        latestOverridesRef.current = next;
        return next;
      });
      saveServer(sk);
    },
    [saveServer]
  );

  // ---------- server-suggested theme ----------
  useEffect(() => {
    const sk = String(activeServerKey || '').trim();
    if (!sk || !userId) {
      setServerTheme({ key: '', theme: null, canEdit: false, loaded: true });
      return undefined;
    }
    let alive = true;
    setServerTheme((prev) => (prev.key === sk ? prev : { key: sk, theme: null, canEdit: false, loaded: false }));
    comsApiGet(`/servers/${encodeURIComponent(sk)}/theme`)
      .then((res) => {
        if (alive) setServerTheme({ key: sk, theme: res && res.theme ? res.theme : null, canEdit: !!(res && res.can_edit), loaded: true });
      })
      .catch(() => {
        if (alive) setServerTheme({ key: sk, theme: null, canEdit: false, loaded: true });
      });
    return () => {
      alive = false;
    };
  }, [activeServerKey, userId]);

  const saveServerTheme = useCallback(async (serverKey, theme) => {
    const sk = String(serverKey || '').trim();
    const res = theme ? await comsApiPut(`/servers/${encodeURIComponent(sk)}/theme`, { theme }) : await comsApiDelete(`/servers/${encodeURIComponent(sk)}/theme`);
    setServerTheme((prev) => (prev.key === sk ? { ...prev, theme: res && res.theme ? res.theme : null, loaded: true } : prev));
    return res;
  }, []);

  // ---------- theme presets ----------
  const refreshThemes = useCallback(async () => {
    const t = await comsApiGet('/me/themes');
    setThemes(Array.isArray(t.themes) ? t.themes : []);
    return t.themes || [];
  }, []);

  const createThemePreset = useCallback(
    async (name, theme, opts = {}) => {
      const res = await comsApiPost('/me/themes', { name, theme, source: opts.source });
      await refreshThemes();
      return res.theme;
    },
    [refreshThemes]
  );

  const updateThemePreset = useCallback(
    async (id, patch) => {
      const res = await comsApiPatch(`/me/themes/${encodeURIComponent(id)}`, patch);
      await refreshThemes();
      // Keep the inline copy used for instant paint in sync when the active custom theme changes.
      if (res && res.theme && latestGlobalRef.current.theme.customId === Number(id)) {
        updateGlobal((p) => ({ ...p, theme: { ...p.theme, custom: res.theme.theme } }));
      }
      return res.theme;
    },
    [refreshThemes, updateGlobal]
  );

  const duplicateThemePreset = useCallback(
    async (id) => {
      const res = await comsApiPost(`/me/themes/${encodeURIComponent(id)}/duplicate`, {});
      await refreshThemes();
      return res.theme;
    },
    [refreshThemes]
  );

  const deleteThemePreset = useCallback(
    async (id) => {
      await comsApiDelete(`/me/themes/${encodeURIComponent(id)}`);
      await refreshThemes();
      if (latestGlobalRef.current.theme.customId === Number(id)) {
        updateGlobal((p) => ({ ...p, theme: { ...p.theme, mode: 'dark', customId: null, custom: null } }));
      }
    },
    [refreshThemes, updateGlobal]
  );

  const applyThemePreset = useCallback(
    (preset) => {
      if (!preset) return;
      updateGlobal((p) => ({ ...p, theme: { ...p.theme, mode: 'custom', customId: preset.id, custom: preset.theme } }));
    },
    [updateGlobal]
  );

  // ---------- effective values ----------
  const activeOverrides = useMemo(() => (activeServerKey && serverOverrides[activeServerKey]) || NO_OVERRIDES, [activeServerKey, serverOverrides]);
  const effective = useMemo(() => resolveEffective({ global: globalPrefs, serverOverrides: activeOverrides, temp }), [globalPrefs, activeOverrides, temp]);
  // Phone-sized screens use the mobile prefs for what a phone shows (density, text size, tabs, pinned banner).
  const isMobileLayout = useMediaQuery(MOBILE_QUERY);
  const shownLayout = useMemo(() => (isMobileLayout ? layoutForMobile(effective.layout, globalPrefs.mobile) : effective.layout), [isMobileLayout, effective.layout, globalPrefs.mobile]);

  const systemDark = useMediaQuery('(prefers-color-scheme: dark)');
  const systemReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const resolvedTheme = useMemo(() => {
    if (themePreview) return resolveTheme(themePreview);
    const choice = effective.themeChoice;
    const g = globalPrefs.theme;
    if (choice === 'server' && serverTheme.theme && themeAccessibility(serverTheme.theme).ok) return resolveTheme(serverTheme.theme);
    if (['dark', 'light', 'oled', 'contrast'].includes(choice)) return resolveTheme(choice);
    if (choice && choice.startsWith('custom:')) {
      const preset = themes.find((t) => `custom:${t.id}` === choice);
      if (preset) return resolveTheme(preset.theme);
    }
    if (g.mode === 'custom') {
      const preset = themes.find((t) => t.id === g.customId);
      return resolveTheme((preset && preset.theme) || g.custom || 'dark', { accent: g.accent });
    }
    return resolveTheme(g.mode === 'system' ? (systemDark ? 'dark' : 'light') : g.mode, { accent: g.accent });
    // systemDark is listed so "Match system" follows OS changes live.
  }, [themePreview, effective.themeChoice, globalPrefs.theme, serverTheme.theme, themes, systemDark]);

  const reduceMotion = globalPrefs.theme.reduceMotion === 'on' || (globalPrefs.theme.reduceMotion === 'system' && systemReducedMotion);
  // Speaking indicators pause on a still frame under reduced motion unless the user asked for them to move.
  const speakingStill = reduceMotion && globalPrefs.theme.speakingMotion !== 'always';
  const focusRing = globalPrefs.theme.focusRing === 'strong' ? 'strong' : 'standard';

  // Paint before the browser draws: theme tokens and layout attributes on <html>.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const vars = buildThemeVars(resolvedTheme);
    applyThemeVars(vars, root);
    writeJson(LAST_THEME_KEY, vars);
    root.setAttribute('data-vx-theme', resolvedTheme.base === 'light' ? 'light' : 'dark');
    root.setAttribute('data-vx-theme-id', resolvedTheme.id);
    root.toggleAttribute('data-vx-gradient', !!vars['--vx-app-bg']);
    root.style.colorScheme = resolvedTheme.base === 'light' ? 'light' : 'dark';
  }, [resolvedTheme]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const l = shownLayout;
    root.setAttribute('data-vx-density', l.density);
    root.setAttribute('data-vx-chat-density', l.chatDensity);
    root.setAttribute('data-vx-userbar', l.userBar);
    root.setAttribute('data-vx-activities-button', l.activitiesButton ? 'on' : 'off');
    root.toggleAttribute('data-vx-reduce-motion', !!reduceMotion);
    root.setAttribute('data-vx-focus', focusRing);
    root.style.setProperty('--vx-msg-scale', String(l.messageScale));
    root.style.setProperty('--vx-ui-scale', String(l.uiScale));
  }, [shownLayout, reduceMotion, focusRing]);

  // Leaving Voxemera (e.g. to a Point Atlas map) restores the page's own styling.
  useEffect(
    () => () => {
      const root = document.documentElement;
      clearThemeVars(root);
      for (const a of ['data-vx-theme', 'data-vx-theme-id', 'data-vx-gradient', 'data-vx-density', 'data-vx-chat-density', 'data-vx-userbar', 'data-vx-activities-button', 'data-vx-reduce-motion', 'data-vx-focus', 'data-vx-layout-editing']) {
        root.removeAttribute(a);
      }
      root.style.removeProperty('--vx-msg-scale');
      root.style.removeProperty('--vx-ui-scale');
      root.style.colorScheme = '';
    },
    []
  );

  // ---------- layout editor session ----------
  // While "Customise layout" is open, edits go to the session layer (previewed live, not saved). Apply writes
  // them to the account; Cancel drops them.
  const tempRef = useRef(temp);
  tempRef.current = temp;

  const beginLayoutEditing = useCallback(() => {
    setLayoutEditing((cur) => cur || { startedAt: Date.now() });
  }, []);

  const setEditValue = useCallback((path, value) => {
    setTemp((prev) => {
      const next = { ...prev, [`layout.${path}`]: value };
      if (path !== 'preset') next['layout.preset'] = 'custom';
      return next;
    });
  }, []);

  const setEditPreset = useCallback((presetId) => {
    setTemp((prev) => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) if (!k.startsWith('layout.')) next[k] = v;
      next['layout.preset'] = presetId;
      return next;
    });
  }, []);

  const resetEdit = useCallback(() => {
    setTemp((prev) => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) if (!k.startsWith('layout.')) next[k] = v;
      const d = clone(DEFAULT_PREFS.layout);
      next['layout.preset'] = 'classic';
      next['layout.sidebar.width'] = d.sidebar.width;
      next['layout.members.width'] = d.members.width;
      next['layout.messageScale'] = d.messageScale;
      next['layout.uiScale'] = d.uiScale;
      return next;
    });
  }, []);

  const finishLayoutEditing = useCallback(
    (apply) => {
      const layer = {};
      for (const [k, v] of Object.entries(tempRef.current)) if (k.startsWith('layout.')) layer[k] = v;
      if (apply && Object.keys(layer).length) {
        updateGlobal((p) => {
          let layout = p.layout;
          if (layer['layout.preset'] && layer['layout.preset'] !== 'custom') layout = applyPresetToLayout(layout, layer['layout.preset']);
          for (const [k, v] of Object.entries(layer)) if (k !== 'layout.preset') layout = setPath(layout, k.slice('layout.'.length), v);
          if (layer['layout.preset'] === 'custom') layout = { ...layout, preset: 'custom' };
          return { ...p, layout };
        });
      }
      setTemp((prev) => {
        const next = {};
        for (const [k, v] of Object.entries(prev)) if (!k.startsWith('layout.')) next[k] = v;
        return next;
      });
      setLayoutEditing(null);
    },
    [updateGlobal]
  );

  const setTempValue = useCallback((key, value) => {
    setTemp((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      loaded,
      syncError,
      globalPrefs,
      serverOverrides,
      activeServerKey,
      setActiveServerKey,
      activeOverrides,
      effective,
      layout: shownLayout,
      isMobileLayout,
      mobilePrefs: globalPrefs.mobile || defaultMobile(),
      setMobileValue,
      setMobilePreset,
      setMobileValues,
      resetMobile,
      resolvedTheme,
      themeBase: resolvedTheme.base === 'light' ? 'light' : 'dark',
      reduceMotion,
      systemReducedMotion,
      speakingStill,
      themes,
      serverTheme,
      temp,
      setTempValue,
      layoutEditing: !!layoutEditing,
      beginLayoutEditing,
      finishLayoutEditing,
      setEditValue,
      setEditPreset,
      resetEdit,
      themePreview,
      setThemePreview,
      updateGlobal,
      setLayoutValue,
      setLayoutPreset,
      resetLayout,
      setThemeValue,
      setTabsValue,
      setServerValue,
      resetServer,
      saveServerTheme,
      refreshThemes,
      createThemePreset,
      updateThemePreset,
      duplicateThemePreset,
      deleteThemePreset,
      applyThemePreset,
    }),
    [
      loaded,
      syncError,
      globalPrefs,
      serverOverrides,
      activeServerKey,
      activeOverrides,
      effective,
      shownLayout,
      isMobileLayout,
      setMobileValue,
      setMobilePreset,
      setMobileValues,
      resetMobile,
      resolvedTheme,
      reduceMotion,
      systemReducedMotion,
      speakingStill,
      themes,
      serverTheme,
      temp,
      setTempValue,
      layoutEditing,
      beginLayoutEditing,
      finishLayoutEditing,
      setEditValue,
      setEditPreset,
      resetEdit,
      themePreview,
      updateGlobal,
      setLayoutValue,
      setLayoutPreset,
      resetLayout,
      setThemeValue,
      setTabsValue,
      setServerValue,
      resetServer,
      saveServerTheme,
      refreshThemes,
      createThemePreset,
      updateThemePreset,
      duplicateThemePreset,
      deleteThemePreset,
      applyThemePreset,
    ]
  );

  return <PersonalisationContext.Provider value={value}>{children}</PersonalisationContext.Provider>;
}

// Outside the provider (tests, Point Atlas pages) components get defaults and no-op setters.
const FALLBACK = (() => {
  const noop = () => {};
  const effective = resolveEffective({ global: DEFAULT_PREFS });
  return {
    loaded: true,
    syncError: '',
    globalPrefs: DEFAULT_PREFS,
    serverOverrides: {},
    activeServerKey: '',
    setActiveServerKey: noop,
    activeOverrides: {},
    effective,
    layout: effective.layout,
    isMobileLayout: false,
    mobilePrefs: DEFAULT_PREFS.mobile,
    setMobileValue: noop,
    setMobilePreset: noop,
    setMobileValues: noop,
    resetMobile: noop,
    resolvedTheme: resolveTheme('dark'),
    themeBase: 'dark',
    reduceMotion: false,
    systemReducedMotion: false,
    speakingStill: false,
    themes: [],
    serverTheme: { key: '', theme: null, canEdit: false, loaded: true },
    temp: {},
    setTempValue: noop,
    layoutEditing: false,
    beginLayoutEditing: noop,
    finishLayoutEditing: noop,
    setEditValue: noop,
    setEditPreset: noop,
    resetEdit: noop,
    themePreview: null,
    setThemePreview: noop,
    updateGlobal: noop,
    setLayoutValue: noop,
    setLayoutPreset: noop,
    resetLayout: noop,
    setThemeValue: noop,
    setTabsValue: noop,
    setServerValue: noop,
    resetServer: noop,
    saveServerTheme: async () => null,
    refreshThemes: async () => [],
    createThemePreset: async () => null,
    updateThemePreset: async () => null,
    duplicateThemePreset: async () => null,
    deleteThemePreset: async () => {},
    applyThemePreset: noop,
  };
})();

export function usePersonalisation() {
  return useContext(PersonalisationContext) || FALLBACK;
}

// Applies the last theme used on this device before React renders (avoids a dark flash for light themes).
export function paintCachedTheme() {
  try {
    const vars = readJson(LAST_THEME_KEY);
    if (vars && typeof vars === 'object') applyThemeVars(vars);
    if (vars && vars['--vx-fg-rgb'] && vars['--vx-fg-rgb'] !== '255,255,255') document.documentElement.setAttribute('data-vx-theme', 'light');
  } catch {}
}

export { prefersReducedMotion };
